// MACHINERY V2 (Stroje) — fleet mutation handlers (M2.2 logic, extracted M2.3).
//
// Duplicate, Restore, Delete, and the per-machine kebab action set. Split out of
// the fleet page when M2.3 added the second view, so the page stays a
// composition and BOTH views get identical behaviour from one place — a row and
// a card must never do different things behind the same menu item.
//
// Nothing here was new logic at extraction time: it was M2.2's handlers moved
// verbatim, including the two review fixes they carry (F5's `queryRef` merge
// base and F6's try/catch around the unwrapped restore). MDEL-FE then added the
// Admin hard delete, whose flow (eligibility read + named confirm + the
// enumerated-blocker toast) lives in the shared useMachineDelete hook so the
// profile runs the identical flow; only the post-delete refresh differs.
//
// EVERY REFRESH GOES THROUGH `fleetFetchQuery` (MDEL-FE review finding 5). The
// handlers below used to pass `queryRef.current` straight to `getMachines`, but
// that object also carries the CLIENT-side facets (`typeId`,
// `manufacturerId`) that FleetPage writes into the shared slice query — sending
// them narrows the response and replaces the complete-set cache with one
// facet's worth of machines, which never self-repairs because the load's effect
// does not watch facets. Read fleetFetchQuery.ts before changing any argument
// below; it was a live defect on duplicate and restore, not just on delete.
//
// RESTORE OPENS THROUGH `useV2Confirm`, NOT `useConfirmation` (review finding
// 1). Not because restore itself is dangerous, but because the shared provider
// is a singleton that swaps an open dialog's contents in place: unless restore
// REGISTERS that it has a dialog open, a slow delete-eligibility read finishing
// a moment later would replace this dialog and turn a click already in motion
// into a hard delete of a different machine. Registering is the point.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { MutableRefObject, useCallback } from "react";
import { NavigateFunction } from "react-router-dom";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine, MachinesQuery } from "../../models/models";
import {
  createMachine,
  getDecommissionedMachines,
  getMachines,
  restoreMachine,
  setError,
  setSuccess,
} from "../../customerMachinerySlice";
import { useV2Confirm } from "../feedback/useV2Confirm";
import { FleetMenuAction } from "./FleetCardMenu";
import { FleetActionContext, buildFleetActions } from "./fleetActions";
import { buildDuplicatePayload } from "./fleetDuplicate";
import { fleetFetchQuery } from "./fleetFetchQuery";
import { useMachineDelete } from "./useMachineDelete";
import { FleetDialogControls } from "./useFleetDialogs";

export interface FleetHandlers {
  buildActions: (m: Machine) => FleetMenuAction[];
}

export const useFleetHandlers = (
  gates: { canWrite: boolean; canAdmin: boolean },
  queryRef: MutableRefObject<MachinesQuery>,
  dialogs: FleetDialogControls,
  navigate: NavigateFunction
): FleetHandlers => {
  const dispatch = useAppDispatch();
  const confirmV2 = useV2Confirm();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const { requestDelete, deleteBusy } = useMachineDelete();
  const { canWrite, canAdmin } = gates;

  /** The active-list refresh, in the ONE shape the load uses. */
  const refreshActive = useCallback(
    () => dispatch(getMachines(fleetFetchQuery(queryRef.current))),
    [dispatch, queryRef]
  );

  const handleDuplicate = useCallback(
    async (m: Machine) => {
      // The v2 payload carries catalogModelId AND locationId — fleetDuplicate.ts.
      // Rows and cards share it, so the fix cannot regress in one view only.
      const payload = buildDuplicatePayload(m);
      if (!payload) {
        dispatch(setError(translate("myMachinery:v2.fleet.duplicateMissingFields")));
        return;
      }
      await dispatch(createMachine(payload));
      await refreshActive();
    },
    [dispatch, refreshActive, translate]
  );

  const handleRestore = useCallback(
    (m: Machine) => {
      // The EXISTING confirm dialog, reused — §9 D4 carries the action, not a
      // redesign of its dialog. Opened through the v2 gate (see the header).
      const opened = confirmV2({
        title: translate("myMachinery:restoreMachine"),
        text: translate("myMachinery:restoreConfirm"),
        confirmButton: translate("myMachinery:restore"),
        // `common.json` has NO top-level `cancel` key — tCommon("cancel")
        // rendered the literal string "cancel" in BOTH locales. The real key
        // is buttons.cancel (en "Cancel" / sk "Zrušiť").
        closeButton: tCommon("buttons.cancel"),
        severity: "neutral",
        // REVIEW F6 — ConfirmationProvider neither awaits nor catches this
        // callback, so an unwrapped rejection became an unhandled promise
        // rejection AND skipped both refreshes. The live handler
        // (Machinery.tsx:676-681) wraps it; so do we. The slice has already
        // stored the error, and MachineryToastHost renders it.
        onConfirm: async () => {
          try {
            await dispatch(restoreMachine(m.id)).unwrap();
            dispatch(setSuccess(translate("myMachinery:restored")));
          } catch {
            // Error surfaced by the slice -> MachineryToastHost.
          }
          await refreshActive();
          await dispatch(getDecommissionedMachines());
        },
      });
      // Refused rather than dropped — a v2 confirmation is already open and
      // replacing it in place is the wrong-machine bug (review finding 1).
      if (!opened) dispatch(setError(translate("myMachinery:v2.delete.confirmBusy")));
    },
    [confirmV2, dispatch, refreshActive, translate, tCommon]
  );

  // Hard delete (MDEL-FE). The flow — eligibility read, the confirm that names
  // the machine, the enumerated-blocker toast, the in-flight guards — is
  // useMachineDelete's; the ONLY fleet-specific part is what happens after a
  // successful delete, which is the same pair of refreshes restore does. The
  // active list is refreshed too because it is what the page shows when the
  // user switches scope back.
  const handleDelete = useCallback(
    (m: Machine) => {
      requestDelete(m, () => {
        refreshActive();
        dispatch(getDecommissionedMachines());
      });
    },
    [requestDelete, dispatch, refreshActive]
  );

  const buildActions = useCallback(
    (m: Machine) => {
      const ctx: FleetActionContext = { canWrite, canAdmin, deleteBusy };
      return buildFleetActions(m, ctx, {
        // Repointed at M2.4 to the v2 profile. Kept identical to FleetPage's
        // openProfile so the kebab entry and the card/row click cannot diverge.
        onProfile: (x) => navigate(`/machinery-v2/${x.id}`),
        onQrCode: dialogs.openQr,
        onCreateServiceReport: (x) => navigate(`/machinery/${x.id}/service-reports/new`),
        onCreateServiceRequest: (x) => navigate(`/service-requests/new?machine=${x.id}`),
        onEditDetails: dialogs.openEdit,
        onDuplicate: handleDuplicate,
        onAssignGroup: dialogs.openAssignGroup,
        onDecommission: dialogs.openDecommission,
        onRestore: handleRestore,
        onDelete: handleDelete,
      });
    },
    [
      canWrite,
      canAdmin,
      deleteBusy,
      dialogs,
      navigate,
      handleDuplicate,
      handleRestore,
      handleDelete,
    ]
  );

  return { buildActions };
};

export default useFleetHandlers;
